import { EnhancedUser } from 'types/profile.types';
import { Cohort, CohortAction, User } from './types';

/**
 * Helper function to convert sample User to EnhancedUser for profile modal
 */
export const convertUserToEnhancedUser = (user: User): EnhancedUser => {
  // Convert educations to PersonalInfo format
  const educations = (user.educations || []).map(edu => ({
    collegeName: edu.school,
    level: edu.degree,
    majors: [edu.field],
    startTime: new Date(edu.graduationYear - 4, 8, 1), // Estimate start date
    endTime: new Date(edu.graduationYear, 5, 1),
    description: `${edu.degree} in ${edu.field}`
  }));

  // Convert works to PersonalInfo format
  const works = (user.works || []).map(work => ({
    companyName: work.company,
    position: work.title,
    field: [work.title.split(' ').pop() || 'Professional'], // Extract field from title
    startTime: work.startDate !== 'Present' ? new Date(work.startDate) : undefined,
    endTime: work.endDate !== 'Present' ? new Date(work.endDate) : undefined,
    description: work.description
  }));

  // Convert links to string array
  const links = (user.links || []).map(link => link.url);

  return {
    uid: user.uid,
    displayName: `${user.firstName} ${user.lastName}`,
    authEmail: user.email,
    emailVerified: true,
    disabled: false,
    customClaims: undefined,
    creationTime: undefined,
    lastSignInTime: undefined,
    status: 'active',
    lastActiveFormatted: 'Recently active',
    privateData: null,
    profile: {
      id: user.uid,
      email: user.email,
      profileImageUrl: user.profileImageUrl || '',
      firstName: user.firstName,
      lastName: user.lastName,
      quote: user.quote || '',
      professional: user.professional || false,
      mentor: false, // Will be set based on context
      locationData: user.locationData,
      educations,
      works,
      activities: [],
      questionResponses: user.questionResponses || [],
      interests: user.interests || [],
      links,
      contacts: [],
      rejectedProfiles: [],
      matches: [],
      lastActive: user.createdAt,
      createdAt: user.createdAt,
      meetings: [],
      referral_code: undefined,
      affiliationInfo: [],
      userObjectives: [],
      userGoals: [],
      userSources: [],
      userStreaks: [],
      prefAIChat: [],
      prefMentorshipChat: [],
      acceptedCards: [],
      rejectedCards: []
    }
  };
};

/**
 * Helper function to convert compatibility score to label
 */
export const getCompatibilityLabel = (score: number): { label: string; color: string } => {
  const percentage = score * 100;

  if (percentage >= 80) {
    return { label: 'Excellent', color: 'text-green-600 dark:text-green-400' };
  } else if (percentage >= 60) {
    return { label: 'High', color: 'text-blue-600 dark:text-blue-400' };
  } else if (percentage >= 40) {
    return { label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400' };
  } else {
    return { label: 'Low', color: 'text-gray-600 dark:text-gray-400' };
  }
};

/**
 * Helper to convert Firestore Timestamp or Date to milliseconds
 */
export const getTime = (timestamp: any): number => {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (timestamp.toDate) return timestamp.toDate().getTime(); // Firestore Timestamp
  return new Date(timestamp).getTime();
};

/**
 * EXAMPLE_COHORT_ID constant
 */
export const EXAMPLE_COHORT_ID = 'example-cohort';

/**
 * Actions whose scheduled/sent state locks participant and match edits.
 * Once any of these is `active` (scheduled) or `completed` (sent), the
 * cohort's Select Participants and Create Matches steps become read-only.
 */
export const LOCK_TRIGGER_ACTIONS: CohortAction['action'][] = [
  'welcomePackage',
  'sendMatches',
  'sendIntroConsent',
  'sendCheckIn',
  'collectFeedback',
];

export type CohortLockReason = 'scheduled' | 'sent';

export interface CohortLockState {
  locked: boolean;
  reason: CohortLockReason | null;
  // All actions that drive the current lock, sorted newest-first. For `sent`,
  // the lock is permanent and only the newest is meaningful for labeling. For
  // `scheduled`, every entry is something the user must cancel to unlock —
  // callers should enumerate all of them in the UI.
  triggeringActions: CohortAction[];
}

const UNLOCKED_STATE: CohortLockState = {
  locked: false,
  reason: null,
  triggeringActions: [],
};

/**
 * Inspect a cohort's actions and decide whether participant/match edits
 * should be locked. `completed` (sent) takes precedence over `active`
 * (scheduled) because a sent lock is permanent.
 *
 * Returns every action matching the winning reason (newest-first), so the
 * banner can enumerate all scheduled sends the user needs to cancel when
 * multiple are pending at once.
 */
export const getCohortLockState = (cohort: Cohort | null | undefined): CohortLockState => {
  if (!cohort || !cohort.actions || cohort.actions.length === 0) {
    return UNLOCKED_STATE;
  }

  // Per action type, keep only the latest entry. This correctly ignores stale
  // `terminated` entries when the same action was re-scheduled or re-sent.
  const latestByAction = LOCK_TRIGGER_ACTIONS
    .map((actionName) => {
      const matching = cohort.actions!.filter((a) => a.action === actionName);
      if (matching.length === 0) return null;
      return matching.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt))[0];
    })
    .filter((a): a is CohortAction => a !== null);

  const sortNewestFirst = (a: CohortAction, b: CohortAction) =>
    getTime(b.createdAt) - getTime(a.createdAt);

  const completed = latestByAction
    .filter((a) => a.status === 'completed')
    .sort(sortNewestFirst);
  if (completed.length > 0) {
    return { locked: true, reason: 'sent', triggeringActions: completed };
  }

  const active = latestByAction
    .filter((a) => a.status === 'active')
    .sort(sortNewestFirst);
  if (active.length > 0) {
    return { locked: true, reason: 'scheduled', triggeringActions: active };
  }

  return UNLOCKED_STATE;
};

/**
 * Human-friendly label for the cohort step associated with a given action.
 * Used in the lock banner copy (e.g., "Cancel the scheduled send in the Connection Email step").
 */
export const getActionStepLabel = (action: CohortAction['action'] | undefined): string => {
  switch (action) {
    case 'welcomePackage':
      return 'Welcome Package';
    case 'sendMatches':
      return 'Connection Email';
    case 'sendIntroConsent':
      return 'Introduce & Connect';
    case 'sendCheckIn':
      return 'Check-in Email';
    case 'collectFeedback':
      return 'Feedback Request';
    default:
      return 'a later step';
  }
};
